import { Rng, designLevel, hashSeed, type DesignSpec, type DesignedLevel } from "@enjoyment/core";
import { EVENTS, type TimelineEvent } from "./events";

export interface ZamanLevel {
  n: number;
  seed: number;
  cardsToPlace: number;
  minYearGap: number;
  events: TimelineEvent[];
}

/** What a numbered level asks for; the events themselves come from the level's seed. */
export interface ZamanParams {
  cards: number;
  /** Smallest gap between two selected years. */
  minGap: number;
  /** Highest fame tier allowed (1 familiar … 3 hard recall). */
  maxTier: 1 | 2 | 3;
  /** Fame tier the selection leans towards. */
  targetTier: number;
}

/**
 * Chapter design (levels 21+): the slot plays like ramp level `effective` in 9–30 (9: every tier is in, 30: the
 * tier mix stops moving; ten cards and a 3-year gap from 19). Twists are the generator's own knobs: a chapter of
 * deep cuts (picks lean towards tier 3, years at least 20 apart so the era still helps) or of famous events (tiers
 * 1–2 only, as in levels 4–8). Both keep the slot's order: a harder slot leans to harder tiers.
 */
export const DESIGN: DesignSpec = { game: "zaman", floor: 9, ceiling: 30, twists: ["deep-cuts", "famous"] };

function rampCards(n: number): number {
  return Math.min(10, 4 + Math.floor((n - 1) / 3));
}

function rampGap(n: number): number {
  if (n <= 3) return 150;
  if (n <= 8) return 80;
  if (n <= 18) return 20;
  return 3;
}

function maxTierForLevel(level: number): 1 | 2 | 3 {
  if (level <= 3) return 1;
  if (level <= 8) return 2;
  return 3;
}

/** The hand-tuned ramp (levels 1–20, and the difficulty scale the designer draws from). */
function rampParams(n: number): ZamanParams {
  const progress = Math.max(0, Math.min(1, (n - 3) / 27));
  return { cards: rampCards(n), minGap: rampGap(n), maxTier: maxTierForLevel(n), targetTier: n <= 3 ? 1 : 1 + 2 * progress };
}

/** Difficulty of ramp level `d.effective`, the chapter's twist on top. */
function designedParams(d: DesignedLevel): ZamanParams {
  const base = rampParams(d.effective);
  switch (d.twist) {
    case "deep-cuts":
      return { ...base, minGap: Math.max(base.minGap, 20), targetTier: base.targetTier + (3 - base.targetTier) / 2 };
    case "famous":
      return { ...base, maxTier: 2, targetTier: 1 + (base.targetTier - 1) / 2 };
    default:
      return base;
  }
}

/** Parameters of a numbered level: the ramp up to level 20, the chapter design from 21. */
export function paramsFor(level: number): ZamanParams {
  const n = Math.max(1, Math.floor(level));
  const d = designLevel(DESIGN, n);
  return d ? designedParams(d) : rampParams(n);
}

/** The ramp level whose difficulty a numbered level plays at: n itself up to 20, the design's `effective` after. */
export function difficultyLevel(level: number): number {
  const n = Math.max(1, Math.floor(level));
  return designLevel(DESIGN, n)?.effective ?? n;
}

export function cardsForLevel(level: number): number {
  return paramsFor(level).cards;
}

export function minimumYearGap(level: number): number {
  return paramsFor(level).minGap;
}

function pickEvents(level: number, seed: number, params: ZamanParams): TimelineEvent[] {
  const { maxTier, targetTier, minGap } = params;
  const count = params.cards + 1;
  const candidates = EVENTS.filter((event) => event.tier <= maxTier).slice().sort((a, b) => a.year - b.year || a.id.localeCompare(b.id));
  const rng = new Rng(seed);
  const tieBreak = new Map(candidates.map((event) => [event.id, rng.next()]));
  const chosen: TimelineEvent[] = [];

  const canFillAfter = (from: number, previousYear: number, needed: number): boolean => {
    let found = 0;
    let lastYear = previousYear;
    for (let i = from; i < candidates.length; i++) {
      const event = candidates[i]!;
      if (event.year - lastYear < minGap) continue;
      found++;
      lastYear = event.year;
      if (found >= needed) return true;
    }
    return false;
  };

  const search = (start: number, previousYear: number): boolean => {
    if (chosen.length === count) return true;
    const slotsLeft = count - chosen.length;
    const eligible: { event: TimelineEvent; index: number; score: number }[] = [];
    for (let i = start; i < candidates.length; i++) {
      const event = candidates[i]!;
      if (event.year - previousYear < minGap) continue;
      const tie = tieBreak.get(event.id) ?? 0;
      eligible.push({ event, index: i, score: Math.abs(event.tier - targetTier) * 2 + tie });
    }
    eligible.sort((a, b) => a.score - b.score || a.event.year - b.event.year);
    for (const candidate of eligible) {
      if (slotsLeft > 1 && !canFillAfter(candidate.index + 1, candidate.event.year, slotsLeft - 1)) continue;
      chosen.push(candidate.event);
      if (search(candidate.index + 1, candidate.event.year)) return true;
      chosen.pop();
    }
    return false;
  };

  if (!search(0, Number.NEGATIVE_INFINITY)) {
    // There is deliberately no silent relaxation: an impossible content set is a build-time error.
    throw new Error(`Zaman level ${level} cannot select ${count} events ${minGap}+ years apart`);
  }
  return chosen.slice().sort((a, b) => a.year - b.year || a.id.localeCompare(b.id));
}

/** Numbered, repeatable level. Seeds are exactly hashSeed(`zaman:${n}`); events are new for every n, also in a chapter. */
export function generateLevel(levelNumber: number): ZamanLevel {
  const n = Math.max(1, Math.floor(levelNumber));
  const seed = hashSeed(`zaman:${n}`);
  const params = paramsFor(n);
  return { n, seed, cardsToPlace: params.cards, minYearGap: params.minGap, events: pickEvents(n, seed, params) };
}
